(async () => {
  const baseComponent = await import(window.theme.modules.baseComponent);

  customElements.define('collection-product-grid', class extends baseComponent.default {

    render() {
      this.hybridLoads = 0;
      this._restoreState();
      this.listenTo('filterChange', (e, data) => {
        this._update(data.html);
      });
      
      this.listenTo('sortChange', (e, data) => {
        this._update(data.html);
      });
      
      this._initDynamicPagination();
      this._bindScrollSaving();
    };

    _initDynamicPagination() {
      this.isLoading = false;
      const paginationWrapper = this.querySelector('.dynamic-pagination');
      if (!paginationWrapper) return;

      const type = paginationWrapper.dataset.type;
      const nextUrl = paginationWrapper.dataset.nextUrl;
      if (!nextUrl) return;

      if (type === 'load_more') {
        const btn = paginationWrapper.querySelector('button');
        if (btn) {
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            if (!this.isLoading) this._loadNextPage(nextUrl, paginationWrapper, btn);
          });
        }
      } else if (type === 'infinite_scroll') {
        const trigger = paginationWrapper.querySelector('.infinite-scroll-trigger');
        if (trigger) {
          const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && !this.isLoading) {
              this._loadNextPage(nextUrl, paginationWrapper, trigger);
            }
          }, { rootMargin: '0px 0px 400px 0px' });
          observer.observe(trigger);
        }
      } else if (type === 'hybrid') {
        const trigger = paginationWrapper.querySelector('.hybrid-trigger');
        const btnContainer = paginationWrapper.querySelector('.hybrid-button-container');
        const btn = btnContainer ? btnContainer.querySelector('button') : null;

        if (this.hybridLoads < 2) {
          if (trigger) {
            const observer = new IntersectionObserver((entries) => {
              if (entries[0].isIntersecting && !this.isLoading) {
                this.hybridLoads++;
                this._loadNextPage(nextUrl, paginationWrapper, trigger);
              }
            }, { rootMargin: '0px 0px 400px 0px' });
            observer.observe(trigger);
          }
        } else {
          // Switch to button mode
          if (trigger) trigger.style.display = 'none';
          if (btnContainer) btnContainer.style.display = 'flex';
          if (btn) {
            btn.addEventListener('click', (e) => {
              e.preventDefault();
              if (!this.isLoading) this._loadNextPage(nextUrl, paginationWrapper, btn);
            });
          }
        }
      }
    };

    async _loadNextPage(url, wrapper, triggerElement) {
      this.isLoading = true;
      const label = wrapper.dataset.label || 'Load More';
      const loadingText = wrapper.dataset.loadingText || 'Loading...';
      if (triggerElement.tagName === 'BUTTON') {
        triggerElement.classList.add('loading');
        triggerElement.setAttribute('aria-busy', 'true');
        const textSpan = triggerElement.querySelector('.\\#button-text');
        if (textSpan) textSpan.textContent = loadingText;
      }

      try {
        const response = await fetch(url);
        const html = await response.text();
        const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
        
        const newGrid = parsedHtml.querySelector('collection-product-grid #main-collection-product-grid');
        const currentGrid = this.querySelector('#main-collection-product-grid');
        
        if (newGrid && currentGrid) {
          // Append new products
          const newProducts = Array.from(newGrid.children);
          newProducts.forEach(child => currentGrid.appendChild(child));
          
          // Replace pagination wrapper
          const newPagination = parsedHtml.querySelector('collection-product-grid .collection-pagination-wrapper');
          const currentPagination = this.querySelector('.collection-pagination-wrapper');
          if (newPagination && currentPagination) {
             currentPagination.replaceWith(newPagination);
          } else if (currentPagination) {
             currentPagination.remove();
          }
          
          // Do NOT rewrite the URL to ?page=N: that turned the collection's history entry
          // into page N, so Back reopened only page N with no way to reach the earlier products.
          
          // Save state for back button restoration
          this._saveState();
          
          // Re-init dynamic pagination for the newly appended pagination wrapper
          this._initDynamicPagination();
        }
      } catch (error) {
        console.error('Error loading next page:', error);
      } finally {
        this.isLoading = false;
        if (triggerElement && triggerElement.tagName === 'BUTTON') {
          triggerElement.classList.remove('loading');
          triggerElement.removeAttribute('aria-busy');
          const textSpan = triggerElement.querySelector('.\\#button-text');
          if (textSpan) textSpan.textContent = label;
        }
      }
    };

    _stateKey() {
      const params = new URLSearchParams(window.location.search);
      params.delete('page');
      const qs = params.toString();
      return 'collectionState_' + window.location.pathname + (qs ? '?' + qs : '');
    }

    _saveState() {
      try {
        sessionStorage.setItem(this._stateKey(), JSON.stringify({
          html: this.innerHTML,
          scrollY: window.scrollY,
          hybridLoads: this.hybridLoads,
          savedAt: Date.now()
        }));
      } catch (e) {}
    }

    _saveScroll() {
      try {
        const key = this._stateKey();
        const raw = sessionStorage.getItem(key);
        if (!raw) return;
        const state = JSON.parse(raw);
        state.scrollY = window.scrollY;
        sessionStorage.setItem(key, JSON.stringify(state));
      } catch (e) {}
    }

    _bindScrollSaving() {
      // pagehide and visibilitychange fire reliably (also on mobile); beforeunload does not
      window.addEventListener('pagehide', () => this._saveScroll());
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') this._saveScroll();
      });
    }

    _restoreState() {
      // Runs synchronously on init. The old pageshow listener was attached after pageshow had
      // already fired (this module loads asynchronously), so restoration never happened.
      const nav = window.performance && performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
      if (!nav || nav.type !== 'back_forward') return;
      try {
        const raw = sessionStorage.getItem(this._stateKey());
        if (!raw) return;
        const state = JSON.parse(raw);
        if (!state || !state.html || Date.now() - state.savedAt > 30 * 60 * 1000) return;
        this.innerHTML = state.html;
        this.hybridLoads = state.hybridLoads || 0;
        if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
        // Clean a leftover ?page=N from history entries created before this fix
        const url = new URL(window.location.href);
        if (url.searchParams.has('page')) {
          url.searchParams.delete('page');
          history.replaceState(null, '', url.pathname + url.search + url.hash);
        }
        const y = state.scrollY || 0;
        requestAnimationFrame(() => window.scrollTo(0, y));
        window.addEventListener('load', () => window.scrollTo(0, y), { once: true });
      } catch (err) {}
    }

    _update(html) {
      // Clear saved state if user changes filters or sorts
      try { sessionStorage.removeItem(this._stateKey()); } catch (e) {}
      this.hybridLoads = 0;
      const productGridSource = new DOMParser().parseFromString(html, 'text/html').getElementById('CollectionProductGrid');
      this.replaceChildren(...productGridSource.childNodes);
      this._initDynamicPagination();
    };

  });
})();
